"use client";

import { motion, type Transition } from "motion/react";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

type BackgroundPosition = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export function AnimatedBackground({
  children,
  className = "",
  defaultValue,
  enableHover = false,
  transition,
}: {
  children: ReactNode;
  className?: string;
  defaultValue: string;
  enableHover?: boolean;
  transition?: Transition;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeValue, setActiveValue] = useState(defaultValue);
  const [position, setPosition] = useState<BackgroundPosition | null>(null);

  // 根据当前导航项更新背景块的位置和尺寸。
  const updatePosition = (target: HTMLElement) => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    setPosition({
      left: targetRect.left - containerRect.left,
      top: targetRect.top - containerRect.top,
      width: targetRect.width,
      height: targetRect.height,
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    const target = container?.querySelector<HTMLElement>(
      `[data-id="${defaultValue}"]`,
    );
    if (target) {
      updatePosition(target);
    } else {
      setPosition(null);
    }
  }, [defaultValue]);

  const handleMouseOver = (event: MouseEvent<HTMLDivElement>) => {
    if (!enableHover) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-id]",
    );
    if (!target || !event.currentTarget.contains(target)) return;
    const value = target.dataset.id;
    if (!value || value === activeValue) return;
    setActiveValue(value);
    updatePosition(target);
  };

  const handleMouseLeave = () => {
    if (!enableHover || activeValue === defaultValue) return;
    if (!defaultValue) {
      setActiveValue(defaultValue);
      setPosition(null);
      return;
    }
    const container = containerRef.current;
    const target = container?.querySelector<HTMLElement>(
      `[data-id="${defaultValue}"]`,
    );
    if (!target) return;
    setActiveValue(defaultValue);
    updatePosition(target);
  };

  return (
    <div
      ref={containerRef}
      className="animated-background"
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
    >
      {position && (
        <motion.div
          aria-hidden="true"
          className={className}
          animate={position}
          initial={false}
          transition={transition}
        />
      )}
      {children}
    </div>
  );
}
